import { useMutation, useApolloClient, ApolloCache } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { TASKS_COUNT_QUERY } from "components/Tasks/schema";
import { gql } from "generated-graphql";
import {
  Frequency,
  SuggestedTasksQuery,
  UpdateSuggestedTaskInput,
} from "generated-graphql/graphql";
import { useCallback, useState } from "react";

import { SUGGESTED_TASKS_COUNT_QUERY, SUGGESTED_TASKS_QUERY } from "./schema";

const ACCEPT_SUGGESTED_TASKS_MUTATION = gql(`
  mutation AcceptSuggestedTasks($input: AcceptSuggestedTasksInput!) {
    acceptSuggestedTasks(input: $input) {
      processedCount
      processedTaskIds
    }
  }
`);

const REJECT_SUGGESTED_TASKS_MUTATION = gql(`
  mutation RejectSuggestedTasks($input: RejectSuggestedTasksInput!) {
    rejectSuggestedTasks(input: $input) {
      processedCount
      processedTaskIds
    }
  }
`);

const UPDATE_SUGGESTED_TASK_MUTATION = gql(`
  mutation UpdateSuggestedTask($input: UpdateSuggestedTaskInput!) {
    updateSuggestedTask(input: $input) {
      id
      title
      description
      startDate
      frequency
      interval
      createdAt
      facilities {
        id
        name
      }
      documents {
        id
        title
      }
    }
  }
`);

type SuggestedTask = SuggestedTasksQuery["suggestedTasks"]["items"][0];

export interface EditSuggestedTaskInput {
  id: string;
  title?: string;
  description?: string | null;
  startDate?: string;
  frequency?: Frequency;
  interval?: number;
  facilityIds?: string[];
  documentIds?: string[];
  isSuggested?: boolean;
}

interface UseHandleSuggestedTasksParams {
  tenantId: string;
}

interface UseHandleSuggestedTasksResult {
  /**
   * Accept a single suggested task
   */
  acceptTask: (task: SuggestedTask) => void;
  /**
   * Dismiss a single suggested task
   */
  dismissTask: (task: Pick<SuggestedTask, "id">) => void;
  /**
   * Accept many suggested tasks
   */
  acceptAll: (tasks: SuggestedTask[]) => void;
  /**
   * Dismiss many suggested tasks
   */
  dismissAll: (tasks: SuggestedTask[]) => void;
  /**
   * Edit a suggested task, you can accept it if you so choose
   */
  editTask: (input: EditSuggestedTaskInput) => void;
  /**
   * Check if a task has been recently accepted, since the last time this hook was called
   */
  isTaskRecentlyAccepted: (taskId: string) => boolean;
  /**
   * Loading state for the mutations
   */
  loading: boolean;
}

/**
 * Simply updates the suggested tasks count in the cache
 * @param cache
 * @param tenantId
 * @param taskIds
 */
function reduceSuggestedTasksCount(cache: ApolloCache<any>, tenantId: string, taskIds: string[]) {
  cache.updateQuery(
    {
      query: SUGGESTED_TASKS_COUNT_QUERY,
      variables: { search: `tenantId:${tenantId}` },
    },
    (data) => {
      return {
        suggestedTasks: {
          count: data?.suggestedTasks?.count ? data?.suggestedTasks?.count - taskIds.length : 0,
        },
      };
    }
  );
}

export function useHandleSuggestedTasks({
  tenantId,
}: UseHandleSuggestedTasksParams): UseHandleSuggestedTasksResult {
  const alerts = useAlerts();
  const client = useApolloClient();

  const [acceptMutation, { loading: acceptLoading }] = useMutation(ACCEPT_SUGGESTED_TASKS_MUTATION);

  const [rejectMutation, { loading: rejectLoading }] = useMutation(REJECT_SUGGESTED_TASKS_MUTATION);

  const [updateMutation, { loading: updateLoading }] = useMutation(UPDATE_SUGGESTED_TASK_MUTATION);

  const [recentlyAcceptedTaskIds, setRecentlyAcceptedTaskIds] = useState<string[]>([]);

  const isTaskRecentlyAccepted = useCallback(
    (taskId: string) => {
      return recentlyAcceptedTaskIds.includes(taskId);
    },
    [recentlyAcceptedTaskIds]
  );

  const acceptTasks = useCallback(
    async (taskIds: string[]) => {
      if (taskIds.length === 0) return;

      try {
        await acceptMutation({
          variables: {
            input: {
              taskIds,
              tenantId,
            },
          },
          optimisticResponse: {
            acceptSuggestedTasks: {
              __typename: "SuggestedTasksMutationResult",
              processedCount: taskIds.length,
              processedTaskIds: taskIds,
            },
          },
          update: (cache, { data }) => {
            const processedTaskIds = data?.acceptSuggestedTasks?.processedTaskIds ?? [];
            // Do not evict the suggested tasks from the cache
            // this is because we are supposed to keep showing the suggested tasks in the UI
            // until the next time the suggested tasks are fetched
            reduceSuggestedTasksCount(cache, tenantId, processedTaskIds);
          },
          refetchQueries: [TASKS_COUNT_QUERY],
        });

        setRecentlyAcceptedTaskIds((prev) => [...new Set([...prev, ...taskIds])]);

        alerts.success(`${taskIds.length} task${taskIds.length > 1 ? "s" : ""} accepted`);
      } catch (error) {
        alerts.error(`Failed to accept task${taskIds.length > 1 ? "s" : ""}`);
      }
    },
    [tenantId, acceptMutation, alerts]
  );

  const rejectTasks = useCallback(
    async (taskIds: string[]) => {
      if (taskIds.length === 0) return;

      try {
        await rejectMutation({
          variables: {
            input: {
              taskIds,
              tenantId,
            },
          },
          optimisticResponse: {
            rejectSuggestedTasks: {
              __typename: "SuggestedTasksMutationResult",
              processedCount: taskIds.length,
              processedTaskIds: taskIds,
            },
          },
          refetchQueries: [SUGGESTED_TASKS_COUNT_QUERY],
          update: (cache, { data }) => {
            const processedTaskIds = data?.rejectSuggestedTasks?.processedTaskIds ?? [];
            for (const taskId of processedTaskIds) {
              cache.evict({
                id: cache.identify({
                  __typename: "SuggestedTask",
                  id: taskId,
                }),
              });
            }
            reduceSuggestedTasksCount(cache, tenantId, processedTaskIds);
          },
        });

        alerts.success(`${taskIds.length} task${taskIds.length > 1 ? "s" : ""} dismissed`);
      } catch (error) {
        alerts.error(`Failed to dismiss task${taskIds.length > 1 ? "s" : ""}`);
      }
    },
    [tenantId, rejectMutation, alerts]
  );

  const acceptTask = useCallback(
    (task: SuggestedTask) => {
      acceptTasks([task.id]);
    },
    [acceptTasks]
  );

  const dismissTask = useCallback(
    (task: Pick<SuggestedTask, "id">) => {
      rejectTasks([task.id]);
    },
    [rejectTasks]
  );

  const acceptAll = useCallback(
    (tasks: SuggestedTask[]) => {
      const taskIds = tasks.map((t) => t.id);
      acceptTasks(taskIds);
    },
    [acceptTasks]
  );

  const dismissAll = useCallback(
    (tasks: SuggestedTask[]) => {
      const taskIds = tasks.map((t) => t.id);
      rejectTasks(taskIds);
    },
    [rejectTasks]
  );

  const editTask = useCallback(
    async (input: EditSuggestedTaskInput) => {
      try {
        const updateInput: UpdateSuggestedTaskInput = {
          id: input.id,
          tenantId,
          title: input.title,
          description: input.description,
          startDate: input.startDate,
          frequency: input.frequency,
          interval: input.interval,
          facilityIds: input.facilityIds,
          documentIds: input.documentIds,
          isSuggested: input.isSuggested,
        };

        await updateMutation({
          variables: {
            input: updateInput,
          },
        });

        if (!input.isSuggested) {
          setRecentlyAcceptedTaskIds((prev) => [...new Set([...prev, input.id])]);
          reduceSuggestedTasksCount(client.cache, tenantId, [input.id]);
        }

        await client.refetchQueries({
          include: [TASKS_COUNT_QUERY],
        });

        alerts.success("Task updated");
      } catch (error) {
        await client.refetchQueries({
          include: [SUGGESTED_TASKS_QUERY],
        });

        alerts.error("Failed to update task");
      }
    },
    [tenantId, client, updateMutation, alerts]
  );

  return {
    acceptTask,
    dismissTask,
    acceptAll,
    dismissAll,
    editTask,
    isTaskRecentlyAccepted,
    loading: acceptLoading || rejectLoading || updateLoading,
  };
}
